// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// LA MACCHINA A STATI DEL FLUSSO (ARCHITETTURA §7.4): un riduttore puro, nessun effetto. Gli effetti (rete, camera,
// localStorage) stanno in App.tsx e si limitano a mandare eventi. Stati = quelli del simulatore. Invarianti provate
// in machine.test.ts: mai `done` senza COMMITTED, da `committing` solo quattro uscite, ogni stato ha un'uscita.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface Problem {
  status: number;
  code: string;
  detail: string;
}

export interface Session {
  token: string;
  iss: string;
  codSoc: string;
  login: string;
  roles: string[];
  expiresAtUtc: string;
  returnUrl: string | null;
  state: string | null;
  /** Il pallet che Quick ha già chiesto all'operatore: con questo Dim non lo chiede una seconda volta. */
  itemId: string | null;
}

export interface ItemInfo {
  idPallet: string;
  validated: boolean;
  codArticolo: string | null;
  descrizione: string | null;
  qta: number | null;
}

export interface GateMetrics {
  blurVariance: number;
  clipLowShare: number;
  clipHighShare: number;
  glareShare: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Il fotogramma scattato: gli stessi byte che il server misurerà e archivierà (mai ricodificati dopo questo punto). */
export interface Frame {
  blob: Blob;
  widthPx: number;
  heightPx: number;
  gates: GateMetrics;
  gravity: Vec3 | null;
  capturedAtUtc: string;
  settings: Record<string, unknown>;
}

export interface LengthDto {
  value: number;
  u95: number;
  display: string;
}

// Assi (ADR-026): frontMm è il lato che si vede dalla corsia, depthMm quello verso il fondo (in Scaffale viene dal tipo di
// bancale, depthAssumed = true), heightFloorToTopMm da terra alla cima. Stessi nomi nelle Corrections.
export interface MeasurementDto {
  profile: string;
  tier: string;
  accuracyClass: string;
  scenario: string;
  depthAssumed: boolean;
  labelMatched: boolean;
  palletTypeMeasured: string | null;
  frontMm: LengthDto | null;
  depthMm: LengthDto | null;
  heightFloorToTopMm: LengthDto | null;
  overhangMm: { front: number; back: number; left: number; right: number } | null;
  volumeMm3: number | null;
  volumeU95Mm3: number | null;
  confidence: number;
  warnings: string[];
}

export interface Candidate {
  sessionId: string;
  expiresAtUtc: string;
  measurement: MeasurementDto;
  algorithmVersion: string;
}

export interface Corrections {
  frontMm?: number;
  depthMm?: number;
  heightFloorToTopMm?: number;
  reason: string;
}

export interface CommitResult {
  measurementId: string;
  commitSeq: number;
  replayed: boolean;
}

export interface Hint {
  code: string;
  hint: string;
}

export type Phase =
  | 'redeem'
  | 'identify'
  | 'lookup'
  | 'guide'
  | 'computing'
  | 'review'
  | 'correct'
  | 'committing'
  | 'done'
  | 'manual'
  | 'networkLost'
  | 'expired'
  | 'fatal';

export interface DimState {
  phase: Phase;
  session: Session | null;
  itemId: string | null;
  item: ItemInfo | null;
  itemWarning: string | null;
  frame: Frame | null;
  candidate: Candidate | null;
  corrections: Corrections | null;
  hint: Hint | null;
  result: CommitResult | null;
  fatal: string | null;
  /** Dove torna RETRY dopo una rete assente: la fase che stava facendo la chiamata. */
  resume: 'computing' | 'committing' | null;
  abandoned: boolean;
}

export type DimEvent =
  | { type: 'REDEEMED'; session: Session }
  | { type: 'REDEEM_FAILED'; problem: Problem }
  | { type: 'ITEM_SCANNED'; itemId: string }
  | { type: 'LOOKUP_DONE'; item: ItemInfo | null }
  | { type: 'LOOKUP_FAILED' }
  | { type: 'FRAME_CAPTURED'; frame: Frame }
  | { type: 'GATE_REJECTED'; hint: Hint }
  | { type: 'CANDIDATE'; candidate: Candidate }
  | { type: 'REJECTED'; hint: Hint }
  | { type: 'UNAVAILABLE'; hint: Hint }
  | { type: 'NETWORK_LOST' }
  | { type: 'RETRY' }
  | { type: 'CONFIRM' }
  | { type: 'CORRECT' }
  | { type: 'CORRECTIONS'; corrections: Corrections }
  | { type: 'BACK' }
  | { type: 'RETAKE' }
  | { type: 'COMMITTED'; result: CommitResult }
  | { type: 'EXPIRED' }
  | { type: 'MANUAL' }
  | { type: 'MANUAL_CANDIDATE'; candidate: Candidate }
  | { type: 'RESUME_PENDING'; session: Session; candidate: Candidate; corrections: Corrections | null }
  | { type: 'NEW_MEASUREMENT' }
  | { type: 'ABANDON' }
  | { type: 'FATAL'; message: string };

export function initialState(): DimState {
  return {
    phase: 'redeem',
    session: null,
    itemId: null,
    item: null,
    itemWarning: null,
    frame: null,
    candidate: null,
    corrections: null,
    hint: null,
    result: null,
    fatal: null,
    resume: null,
    abandoned: false,
  };
}

/** Pulizia di ciò che riguarda una misura (non la sessione): si usa per Rifai, Nuova misura, Annulla. */
function cleared(s: DimState): DimState {
  return { ...s, itemId: null, item: null, itemWarning: null, frame: null, candidate: null, corrections: null, hint: null, result: null, resume: null };
}

export function reduce(s: DimState, e: DimEvent): DimState {
  // Uscite valide da qualsiasi stato non terminale: abbandono e guasto fatale.
  if (e.type === 'ABANDON' && s.phase !== 'done') {
    return { ...s, phase: 'done', abandoned: true, hint: null };
  }
  if (e.type === 'FATAL') {
    return { ...s, phase: 'fatal', fatal: e.message };
  }
  if (e.type === 'RESUME_PENDING') {
    // Ricarico della pagina con un commit in sospeso: si riprende da dove si era, la sessione server vive 30 minuti.
    return { ...s, phase: 'committing', session: e.session, candidate: e.candidate, corrections: e.corrections, itemId: null };
  }

  switch (s.phase) {
    case 'redeem':
      // Il pallet scansionato in Quick arriva dal server (ctx del codice): niente seconda scansione, si va dritti al controllo del pallet.
      if (e.type === 'REDEEMED') return e.session.itemId ? { ...cleared(s), phase: 'lookup', session: e.session, itemId: e.session.itemId } : { ...s, phase: 'identify', session: e.session };
      if (e.type === 'REDEEM_FAILED') return { ...s, phase: 'fatal', fatal: e.problem.detail };
      return s;

    case 'identify':
      if (e.type === 'ITEM_SCANNED') return { ...cleared(s), phase: 'lookup', itemId: e.itemId };
      return s;

    case 'lookup':
      if (e.type === 'LOOKUP_DONE')
        return { ...s, phase: 'guide', item: e.item, itemWarning: e.item && !e.item.validated ? 'Pallet non verificato in EMS' : null };
      if (e.type === 'LOOKUP_FAILED') return { ...s, phase: 'guide', item: null, itemWarning: 'EMS non raggiungibile: pallet non verificato' };
      return s;

    case 'guide':
      if (e.type === 'FRAME_CAPTURED') return { ...s, phase: 'computing', frame: e.frame, hint: null, resume: 'computing' };
      if (e.type === 'GATE_REJECTED') return { ...s, hint: e.hint };
      if (e.type === 'MANUAL') return { ...s, phase: 'manual', hint: null };
      return s;

    case 'computing':
      if (e.type === 'CANDIDATE') return { ...s, phase: 'review', candidate: e.candidate, hint: null };
      if (e.type === 'REJECTED') return { ...s, phase: 'guide', frame: null, hint: e.hint };
      if (e.type === 'UNAVAILABLE') return { ...s, phase: 'manual', frame: null, hint: e.hint };
      if (e.type === 'NETWORK_LOST') return { ...s, phase: 'networkLost', resume: 'computing' };
      return s;

    case 'review':
      if (e.type === 'CONFIRM') return { ...s, phase: 'committing', corrections: null, resume: 'committing' };
      if (e.type === 'CORRECT') return { ...s, phase: 'correct' };
      if (e.type === 'RETAKE') return { ...s, phase: 'guide', frame: null, candidate: null, hint: null };
      return s;

    case 'correct':
      if (e.type === 'CORRECTIONS') return { ...s, phase: 'committing', corrections: e.corrections, resume: 'committing' };
      if (e.type === 'BACK') return { ...s, phase: 'review' };
      return s;

    case 'manual':
      if (e.type === 'MANUAL_CANDIDATE') return { ...s, phase: 'committing', candidate: e.candidate, corrections: null, resume: 'committing' };
      if (e.type === 'BACK') return { ...s, phase: 'guide', hint: null };
      return s;

    case 'committing':
      if (e.type === 'COMMITTED') return { ...s, phase: 'done', result: e.result, resume: null };
      if (e.type === 'NETWORK_LOST') return { ...s, phase: 'networkLost', resume: 'committing' };
      if (e.type === 'EXPIRED') return { ...s, phase: 'expired', candidate: null, corrections: null, resume: null };
      return s;

    case 'networkLost':
      if (e.type === 'RETRY') return { ...s, phase: s.resume ?? 'guide' };
      return s;

    case 'expired':
      if (e.type === 'RETAKE') return { ...s, phase: 'guide', frame: null, hint: { code: 'capture-session-expired', hint: 'Sessione scaduta: rifai la foto' } };
      return s;

    case 'done':
      if (e.type === 'NEW_MEASUREMENT') return { ...cleared(s), phase: 'identify', abandoned: false };
      return s;

    case 'fatal':
      return s;
  }
}

/** Gli eventi che fanno uscire da ogni stato: la tabella che il test di copertura legge. */
export const EXITS: Record<Phase, ReadonlyArray<DimEvent['type']>> = {
  redeem: ['REDEEMED', 'REDEEM_FAILED'],
  identify: ['ITEM_SCANNED', 'ABANDON'],
  lookup: ['LOOKUP_DONE', 'LOOKUP_FAILED'],
  guide: ['FRAME_CAPTURED', 'MANUAL', 'ABANDON'],
  computing: ['CANDIDATE', 'REJECTED', 'UNAVAILABLE', 'NETWORK_LOST'],
  review: ['CONFIRM', 'CORRECT', 'RETAKE', 'ABANDON'],
  correct: ['CORRECTIONS', 'BACK'],
  manual: ['MANUAL_CANDIDATE', 'BACK', 'ABANDON'],
  committing: ['COMMITTED', 'NETWORK_LOST', 'EXPIRED', 'FATAL'],
  networkLost: ['RETRY', 'ABANDON'],
  expired: ['RETAKE', 'ABANDON'],
  done: ['NEW_MEASUREMENT'],
  fatal: ['ABANDON'],
};
